import { useCallback, useEffect, useState } from "react";
import { gaEvent, trackPurchase } from "../lib/ga";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, CheckCircle2, Download, HardHat, Loader2, Lock,
  ShieldCheck, Sparkles, XCircle,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { useAnalysisRunner, AnalysisProgressModal } from "../lib/analysisRunner";
import { BuyCreditsModal } from "../components/founder/ReadinessReportCard";
import { maybeStartProjectReportTourOnFirstVisit } from "../lib/tour";
import VerifiedReadinessUpsell from "../components/founder/VerifiedReadinessUpsell";
import { VentureSwitcher } from "../components/founder/VentureSwitcher";

const PR_PILLAR_NAMES = { "PR-1": "Sponsor", "PR-2": "Land & Permits", "PR-3": "Technical & Cost", "PR-4": "Revenue & Offtake", "PR-5": "Capital Structure & Viability", "PR-6": "Risk, Security & Compliance" };
const pillarName = (code) => PR_PILLAR_NAMES[String(code || "").trim()] || code;
const humanPR = (t) => String(t || "").replace(/\bPR-([1-6])\b/g, (_, n) => PR_PILLAR_NAMES[`PR-${n}`] || `PR-${n}`).replace(/\bPRS\b/g, "score");


const NAV = [
  { to: "/project/intake", label: "Project Intake" },
  { to: "/project/report", label: "Readiness Report" },
  { to: "/founder/team", label: "Team" },
  { to: "/founder/tutorial", label: "Tutorial" },
];

const BAND_STYLE = {
  bankable: "bg-emerald-600 text-white",
  investment_grade: "bg-[#00828F] text-white",
  promising: "bg-amber-500 text-white",
  developing: "bg-orange-500 text-white",
  pre_readiness: "bg-rose-600 text-white",
};

const VERDICT_STYLE = {
  viable: { label: "Viable", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  possible: { label: "Possible", cls: "bg-amber-50 border-amber-200 text-amber-700" },
  not_yet: { label: "Not yet", cls: "bg-rose-50 border-rose-200 text-rose-700" },
};

const SEV_STYLE = {
  HIGH: "bg-rose-100 text-rose-700",
  MEDIUM: "bg-amber-100 text-amber-700",
  LOW: "bg-neutral-100 text-neutral-600",
};

function PillarBars({ pillars }) {
  return (
    <div className="space-y-3" data-testid="prs-pillar-bars">
      {Object.entries(pillars || {}).map(([k, p]) => (
        <div key={k}>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="font-semibold text-neutral-800">
              {p.label}
            </span>
            <span className="flex-shrink-0 text-xs text-neutral-500">
              {p.score}/100 · weight {(p.weight * 100).toFixed(0)}%
            </span>
          </div>
          <div className="mt-1 h-2.5 rounded-full bg-neutral-100">
            <div className="h-2.5 rounded-full bg-[#00828F] transition-all"
                 style={{ width: `${p.score}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProjectReport() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/project/readiness/report.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const slug = (report?.company?.company_name || "project").replace(/\s+/g, "-").toLowerCase();
      a.download = `clinq-project-readiness-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      gaEvent("report_download", { report: "project_readiness" });
    } catch (_) {
      toast.error("PDF download failed — please try again");
    } finally { setPdfBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      // Post-payment poll: Paystack redirects back here with ?reference=.
      const params = new URLSearchParams(window.location.search);
      const sid = params.get("reference") || params.get("trxref");
      if (sid) {
        try {
          const { data: pollData } = await api.post(`/founder/credits/poll/${sid}`);
          if (pollData?.payment_status === "paid") {
            trackPurchase(pollData, { report: "Project Readiness Report" });
            toast.success("Payment received — your Project Readiness Report is unlocked");
            try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
          }
        } catch (_e) { /* ignore */ }
        window.history.replaceState({}, "", window.location.pathname);
      }
      const { data: intake } = await api.get("/project/intake");
      if (!intake?.analysis_exists) { nav("/project/intake", { replace: true }); return; }
      const { data } = await api.get("/project/readiness/report");
      setReport(data);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404 || st === 409) nav("/project/intake", { replace: true });
    } finally { setLoading(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);
  const reportLoaded = !!report;
  useEffect(() => {
    if (reportLoaded) maybeStartProjectReportTourOnFirstVisit();
  }, [reportLoaded]);

  const runner = useAnalysisRunner({
    startEndpoint: "/project/analyze",
    onDone: () => { toast.success("Fresh score is in"); load(); },
    onError: (msg) => {
      if ((msg || "").toLowerCase().includes("credit")) {
        if (report?.tier) setBuyOpen(true);
        else { toast.info("Tell us how much you are raising in the intake first"); nav("/project/intake"); }
        return;
      }
      toast.error(msg || "Re-run failed");
    },
  });

  if (loading) {
    return (
      <AppShell nav={NAV}>
        <div className="flex items-center gap-2 py-20 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading your Project Readiness Report…
        </div>
      </AppShell>
    );
  }
  if (!report) return <AppShell nav={NAV}><div /></AppShell>;

  const r = report;
  const unlocked = r.unlocked;
  const costMissing = !r.tier;
  const openBuy = () => {
    if (costMissing) {
      toast.info("Tell us how much you are raising in the intake first — it sets your report price tier");
      nav("/project/intake");
      return;
    }
    setBuyOpen(true);
  };

  return (
    <AppShell nav={NAV}>
      <AnalysisProgressModal busy={runner.busy} phase={runner.phase}
        testIdPrefix="prs-rerun" title="Re-scoring your project" />
      {buyOpen && r.tier && (
        <BuyCreditsModal tier={r.tier} onClose={() => setBuyOpen(false)} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
          <HardHat className="h-5 w-5 text-amber-700" />
        </span>
        <div className="min-w-0">
          <h1 className="min-w-0 break-words text-2xl font-bold text-neutral-900 sm:text-3xl" data-testid="prs-report-title">
            {r.company?.company_name || "Project Readiness Report"}
          </h1>
          <p className="text-sm text-neutral-600">
            {r.company?.project_category ? `${r.company.project_category} · ` : ""}
            {r.company?.location ? `${r.company.location} · ` : ""}Project Readiness Score
          </p>
        </div>
        <VentureSwitcher />
      </div>

      {/* Score hero */}
      <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7" data-testid="prs-score-hero">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex h-28 w-28 flex-shrink-0 flex-col items-center justify-center rounded-full border-8 border-[#E5EEF1]">
            <span className="text-4xl font-extrabold text-[#004B62]" data-testid="prs-score-value">{r.readiness_score}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">/ 100</span>
          </div>
          <div className="min-w-0 flex-1">
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${BAND_STYLE[r.band] || "bg-neutral-200"}`}
                  data-testid="prs-band-chip">
              {r.band_label}
            </span>
            {r.verified?.status === "verified" && (
              <span data-testid="prs-verified-chip"
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified · {r.verified.verified_score}/100
              </span>
            )}
            <p className="mt-2 text-sm text-neutral-700">{r.headline}</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-500">
              <span title="At this project stage the score cannot exceed this number until the next milestone is reached">Stage: <b className="text-neutral-700">{r.stage_label}</b> (max score at this stage: {r.stage_cap})</span>
              <span>Scored the way a <b className="text-neutral-700">{r.funder_type_label}</b> funder would weigh it</span>
              {r.cap_applied && (
                <span className="text-amber-700" data-testid="prs-cap-note">
                  Raw score {r.score_uncapped} → limited to {r.score_cap} by deal-breaker checks and project stage
                </span>
              )}
            </div>
          </div>
          <button data-testid="prs-rerun-btn" disabled={runner.busy}
            onClick={() => (r.credits_balance > 0 ? runner.run() : openBuy())}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#00828F] bg-white px-4 py-2 text-xs font-semibold text-[#00828F] hover:bg-[#E5EEF1] disabled:opacity-60">
            <Sparkles className="h-3.5 w-3.5" />
            {r.credits_balance > 0 ? `Re-run (1 credit · ${r.credits_balance} left)` : "Unlock credits to re-run"}
          </button>
          {unlocked && (
            <button data-testid="prs-download-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-[#004B62] px-4 py-2 text-xs font-semibold text-white shadow hover:bg-[#003a4d] disabled:opacity-60">
              {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Download PDF
            </button>
          )}
        </div>
        {r.completeness?.is_provisional && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="prs-provisional-banner">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">
              Provisional score — {r.completeness.missing_core?.length || 0} core answers and{" "}
              {r.completeness.pillars_missing_doc?.length || 0} pillars are missing evidence.
              Completing them in the <button className="font-semibold underline" onClick={() => nav("/project/intake")}>intake</button> lifts your Project Readiness Score.
            </p>
          </div>
        )}
      </section>

      {/* Pillar breakdown — free tier */}
      <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7">
        <h2 className="text-base font-bold text-neutral-900 sm:text-lg">Score breakdown — six pillars</h2>
        <p className="mt-1 text-xs text-neutral-500">
          Pillar scores × {r.funder_type_label} weights = your Project Readiness Score. Every point is traceable.
        </p>
        <div className="mt-5"><PillarBars pillars={r.pillar_scores} /></div>
      </section>

      {(unlocked || (r.verified?.status && r.verified.status !== "none")) && (
        <VerifiedReadinessUpsell project verified={r.verified}
                                 indicativeScore={r.readiness_score} />
      )}

      {!unlocked ? (
        <section className="mt-5 rounded-2xl border border-[#00828F]/30 bg-[#E5EEF1] p-6 sm:p-8 text-center" data-testid="prs-unlock-cta">
          <Lock className="mx-auto h-8 w-8 text-[#00828F]" />
          <h2 className="mt-3 text-lg font-bold text-neutral-900">Unlock your full Project Readiness Report</h2>
          <p className="mx-auto mt-1 max-w-lg text-sm text-neutral-600">
            Evidence-grounded strengths & gaps, the ten hard-check results, your capital
            structure verdicts (bank / DFI / equity / grant), and a 90-day improvement roadmap.
          </p>
          {costMissing ? (
            <>
              <p className="mx-auto mt-3 max-w-md text-xs font-semibold text-amber-700" data-testid="prs-cost-missing-note">
                Report pricing is based on how much you are raising — add it in the intake (Q25) to see your price and unlock.
              </p>
              <button data-testid="prs-unlock-btn" onClick={() => nav("/project/intake")}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-6 py-3 text-sm font-semibold text-white shadow hover:bg-[#003a4d]">
                Enter how much I am raising <ArrowRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button data-testid="prs-unlock-btn" onClick={openBuy}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-6 py-3 text-sm font-semibold text-white shadow hover:bg-[#003a4d]">
              Unlock full report — KES {Number(r.tier.price_usd).toLocaleString()} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </section>
      ) : (
        <>
          {/* Hard checks */}
          <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7" data-testid="prs-hard-checks">
            <h2 className="text-base font-bold text-neutral-900 sm:text-lg">Deal-breaker checks</h2>
            <p className="mt-1 text-xs text-neutral-500">Project finance has true deal-breakers — failed checks cap your Project Readiness Score or block funder categories.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {(r.hard_checks || []).map((c) => (
                <div key={c.id} className={"flex items-start gap-2.5 rounded-xl border px-3.5 py-3 " +
                  (c.passed ? "border-neutral-200 bg-white" : "border-rose-200 bg-rose-50")}>
                  {c.passed
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    : <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />}
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-neutral-800">{c.label}</p>
                    {!c.passed && <p className="mt-0.5 text-xs text-rose-700">{humanPR(c.consequence)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Capital structure verdicts */}
          <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7" data-testid="prs-verdicts">
            <h2 className="text-base font-bold text-neutral-900 sm:text-lg">Capital structure assessment</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Object.entries(r.verdicts || {}).map(([ft, v]) => {
                const vs = VERDICT_STYLE[v.verdict] || VERDICT_STYLE.not_yet;
                const label = { debt_bank: "Bank Debt", debt_dfi: "DFI Debt", equity: "Equity", grant: "Grant / Blended" }[ft] || ft;
                return (
                  <div key={ft} className="rounded-xl border border-neutral-200 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">{label}</p>
                    <p className="mt-1 text-2xl font-extrabold text-[#004B62]">{v.score}</p>
                    <span className={`mt-1.5 inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${vs.cls}`}>{vs.label}</span>
                    {v.blockers?.length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-[11px] text-rose-700">
                        {v.blockers.map((b, i) => <li key={i}>• {b}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Strengths & gaps */}
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="prs-strengths">
              <h2 className="flex items-center gap-2 text-base font-bold text-neutral-900">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> Strengths
              </h2>
              <ul className="mt-3 space-y-2.5">
                {(r.strengths || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" /> {s}
                  </li>
                ))}
                {(r.strengths || []).length === 0 && (
                  <li className="text-sm text-neutral-500">No evidence-grounded strengths yet — upload documents and re-run.</li>
                )}
              </ul>
            </section>
            <section className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="prs-gaps">
              <h2 className="flex items-center gap-2 text-base font-bold text-neutral-900">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Gaps & weaknesses
              </h2>
              <ul className="mt-3 space-y-3">
                {(r.gaps || []).map((g, i) => (
                  <li key={i} className="rounded-xl border border-neutral-100 bg-neutral-50 p-3">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                      <span className="rounded bg-[#E5EEF1] px-1.5 py-0.5 text-[#00828F]">{pillarName(g.pillar)}</span>
                      <span className={`rounded px-1.5 py-0.5 ${SEV_STYLE[g.severity] || SEV_STYLE.LOW}`}>{g.severity}</span>
                      {g.lift && <span className="text-emerald-700">{g.lift}</span>}
                    </div>
                    <p className="mt-1.5 text-sm font-semibold text-neutral-800">{g.gap}</p>
                    <p className="mt-0.5 text-xs text-neutral-600">Fix: {g.action}</p>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* 90-day roadmap */}
          <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7" data-testid="prs-roadmap">
            <h2 className="text-base font-bold text-neutral-900 sm:text-lg">90-day improvement roadmap</h2>
            <div className="mt-4 space-y-3">
              {(r.roadmap || []).map((m, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#004B62] text-xs font-bold text-white">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-800">
                      {m.milestone}
                      <span className="ml-2 text-[11px] font-bold text-[#00828F]">{pillarName(m.pillar)}</span>
                      {m.timeframe && <span className="ml-2 text-[11px] font-medium text-neutral-400">{m.timeframe}</span>}
                      {m.lift && <span className="ml-2 text-[11px] font-bold text-emerald-700">{m.lift}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-600">{m.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <p className="mt-6 max-w-3xl text-[11px] leading-relaxed text-neutral-400" data-testid="prs-disclaimer">
        {r.disclaimer}
      </p>
    </AppShell>
  );
}
